import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  toGridFilterStartOfDay,
  toGridFilterEndOfDay,
  calculateDateRange,
  getGridDateTimeMode,
} from '../../utils';
import type { DateFilterRendererProps } from './DateRangeFilter';
import { CustomDatePicker, DATE_PICKER_MODE } from '../../../../components/common/CustomDatePicker';

export const DateRangeFilterRenderer = ({
  command,
  onDateRangeChange,
  initialDateRange,
}: DateFilterRendererProps) => {
  const dateTimeMode = getGridDateTimeMode(command.settings);

  const initialDates = useMemo(() => {
    if (initialDateRange?.startDate && initialDateRange.endDate) {
      return {
        start: dayjs(initialDateRange.startDate).toDate(),
        end: dayjs(initialDateRange.endDate).toDate(),
      };
    }
    const range = command.defaultRange ?? 'Current month';
    const { startDate: start, endDate: end } = calculateDateRange(range);
    return {
      start: dayjs(start).toDate(),
      end: dayjs(end).toDate(),
    };
  }, [initialDateRange?.startDate, initialDateRange?.endDate, command.defaultRange]);
  const [startDate, setStartDate] = useState<Date | null>(initialDates.start);
  const [endDate, setEndDate] = useState<Date | null>(initialDates.end);

  useEffect(() => {
    setStartDate(initialDates.start);
    setEndDate(initialDates.end);
  }, [initialDates]);

  const handleStartDateChange = (newDate: Date | null) => {
    setStartDate(newDate);
    if (newDate && endDate) {
      onDateRangeChange(
        toGridFilterStartOfDay(dayjs(newDate), dateTimeMode),
        toGridFilterEndOfDay(dayjs(endDate), dateTimeMode)
      );
    }
  };

  const handleEndDateChange = (newDate: Date | null) => {
    setEndDate(newDate);
    if (startDate && newDate) {
      onDateRangeChange(
        toGridFilterStartOfDay(dayjs(startDate), dateTimeMode),
        toGridFilterEndOfDay(dayjs(newDate), dateTimeMode)
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <CustomDatePicker
        label="Start Date"
        value={startDate}
        mode={DATE_PICKER_MODE.DATE}
        onChange={handleStartDateChange}
      />
      <CustomDatePicker
        label="End Date"
        value={endDate}
        mode={DATE_PICKER_MODE.DATE}
        onChange={handleEndDateChange}
      />
    </Box>
  );
};
